import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import CountdownBadge from './CountdownBadge';
import PrimaryMobileButton from './PrimaryMobileButton';
import SecondaryMobileButton from './SecondaryMobileButton';

const FeaturedEventCard = ({ event, isSaved, onSaveToggle, isRegistered }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(dateStr);
  };

  const startDate = parseLocalDate(event.start);
  const endDate = parseLocalDate(event.end);

  const formatDateRange = () => {
    if (!startDate) return '';
    const opts = { month: 'short', day: 'numeric' };
    if (endDate && endDate.toDateString() !== startDate.toDateString()) {
      return `${startDate.toLocaleDateString('en-US', opts)} - ${endDate.toLocaleDateString('en-US', opts)}`;
    }
    return startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const imageUrl = event.image_url || event.image;

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-card bg-white dark:bg-gray-800">
      {/* Hero image */}
      <div className="relative w-full h-52 bg-gray-200 dark:bg-gray-700 overflow-hidden">
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700" />
        )}
        {imageError ? (
          <div className="flex items-center justify-center w-full h-full bg-gradient-to-br from-primary to-primary-dark">
            <span className="text-white/50 text-4xl font-display font-bold">ACP</span>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={event.event || event.title || 'Featured event'}
            className={`w-full h-full object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            loading="lazy"
          />
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        {/* Save button */}
        {onSaveToggle && (
          <button
            onClick={() => onSaveToggle(event.id)}
            className="absolute top-3 right-3 flex items-center justify-center w-9 h-9 rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 transition-colors"
            aria-label={isSaved ? 'Remove from saved' : 'Save event'}
          >
            <svg
              className={`w-5 h-5 ${isSaved ? 'text-amber-400 fill-current' : 'text-white'}`}
              fill={isSaved ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>
        )}
        {/* Text overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary/90 text-white">
              {event.organization}
            </span>
            {isRegistered && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500/90 text-white">
                Registered
              </span>
            )}
          </div>
          <h2 className="font-display text-xl font-bold text-white leading-tight mb-1 line-clamp-2">
            {event.event || event.title}
          </h2>
          <div className="flex items-center gap-3 text-sm text-white/80">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formatDateRange()}
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {event.city}, {event.state || ''}
            </span>
          </div>
        </div>
      </div>
      {/* Action bar */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <CountdownBadge targetDate={event.start} />
        </div>
        <div className="flex gap-2">
          {isRegistered ? (
            <>
              <Link to="/my-tickets" className="flex-1">
                <PrimaryMobileButton fullWidth size="sm" icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                }>
                  View Ticket
                </PrimaryMobileButton>
              </Link>
              <Link to={`/events/${event.id}`} className="flex-1">
                <SecondaryMobileButton fullWidth size="sm">
                  Details
                </SecondaryMobileButton>
              </Link>
            </>
          ) : (
            <>
              <Link to={`/events/${event.id}`} className="flex-1">
                <PrimaryMobileButton fullWidth size="sm">
                  View Details
                </PrimaryMobileButton>
              </Link>
              <SecondaryMobileButton
                size="sm"
                onClick={() => onSaveToggle?.(event.id)}
                icon={
                  <svg className={`w-4 h-4 ${isSaved ? 'text-amber-400 fill-current' : ''}`} fill={isSaved ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                }
              >
                {isSaved ? 'Saved' : 'Save'}
              </SecondaryMobileButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturedEventCard;
